import { Component, effect, inject, input, signal, ElementRef, ViewChild, AfterViewInit, OnDestroy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import * as L from 'leaflet';

import {
  InfoCardComponent,
  DataSectionComponent,
  DataRowComponent,
  PhotoEvidenceComponent,
  ButtonComponent,
  InfoCard,
  DataSection,
  PersonalReference,
  Location,
  TicketClosingField,
  ApplicantRecordService,
  TicketService,
  TicketComment,
  SnackbarService,
  TranslationService,
  ScoreService,
  TicketDetailStore,
} from '../../../../shared';
import { mapMarksColors } from '../../../../shared/data';

@Component({
  selector: 'app-applicant-record',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    FormsModule,
    TranslatePipe,
    InfoCardComponent,
    DataSectionComponent,
    DataRowComponent,
    PhotoEvidenceComponent,
    ButtonComponent,
  ],
  templateUrl: './applicant-record.component.html',
})
export class ApplicantRecordComponent implements AfterViewInit, OnDestroy {
  // Services
  private applicantRecordService = inject(ApplicantRecordService);
  private ticketService = inject(TicketService);
  private snackbarService = inject(SnackbarService);
  private translationService = inject(TranslationService);
  private scoreService = inject(ScoreService);
  private store = inject(TicketDetailStore);

  @ViewChild('mapContainer') mapContainer!: ElementRef;

  // Inputs
  public ticketId = input.required<number>();
  public validationTicketId = input<number>(0);
  public locationDetail = input<Location | null>(null);
  public loading = input(true);
  public isActionLoading = input(false);
  public canPerformAction = input(false);
  // Closing Fields and Evidence
  public closingFields = computed<TicketClosingField[]>(() => this.store.closingFields());
  public evidenceSections = this.store.photoEvidenceSections;
  public photoEvidenceLoading = this.store.photoEvidenceLoading;
  // Applicant Record Data
  public infoCards = signal<InfoCard[]>([]);
  public infoCardsReady = signal(false);
  // User Profile Data
  public userProfile = signal<DataSection | null>(null);
  public userProfileReady = signal(false);
  // Location Data
  public locationData = signal<DataSection | null>(null);
  public locationDataReady = signal(false);
  // Personal References Data
  public personalReferences = signal<PersonalReference[]>([]);
  public personalReferencesReady = signal(false);
  // Comments Data
  public commentsReady = signal(false);
  public commentsLoading = signal(false);
  public commentText = signal('');
  public isSubmittingComment = signal(false);
  public comments = signal<TicketComment[]>([]);
  // Map and Coordinates
  public coordinates = signal<{ user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null }>({ user: null, formulario: null, evidencias: null });
  public radiusMeters = this.scoreService.radiusMeters;
  public markerColors = { main: mapMarksColors[3], user: mapMarksColors[0], formulario: mapMarksColors[1], evidencias: mapMarksColors[2] };
  // Map and Markers
  private map: L.Map | null = null;
  private markers: L.CircleMarker[] = [];
  private locationCircle: L.Circle | null = null;

  constructor() {
    // Carga evidencia fotográfica cuando closingFields cambia
    effect(() => {
      const fields = this.store.closingFields();
      if (fields && fields.length > 0) {
        this.store.loadPhotoEvidence(fields);
      }
    });

    effect(() => {
      const fields = this.closingFields();
      if (fields.length > 0) {
        const coords = this.applicantRecordService.buildCoordinates(fields);
        this.coordinates.set(coords);
        this.buildInfoCards(fields, coords, this.locationDetail());
        this.buildUserProfile(fields);
        this.buildPersonalReferences(fields);
      } 
    });

    effect(() => {
      const fields = this.closingFields();
      const location = this.locationDetail();
      if (fields.length > 0) {
        this.buildLocationData(fields, location as Location);
      }
    });

    effect(() => {
      const sections = this.evidenceSections();
      if (sections.length > 0) {
        this.photoEvidenceLoading.set(false);
      }
    });

    effect(() => {
      const validationId = this.validationTicketId();
      if (validationId) {
        this.loadComments();
      } else {
        this.commentsReady.set(false);
        this.commentsLoading.set(false);
      }
    });

    effect(() => {
      const ready = this.locationDataReady();
      if (ready && !this.map) {
        setTimeout(() => this.initMapIfReady(), 0);
      } else if (ready && this.map) {
        this.map.remove();
        this.map = null;
        setTimeout(() => this.initMapIfReady(), 0);
      }
    });

    effect(() => {
      const coords = this.coordinates();
      const location = this.locationDetail();
      if (this.map) {
        this.updateMapMarkers(coords, location);
      }
    });

    effect(() => {
      const radius = this.radiusMeters();
      if (this.locationCircle) {
        this.locationCircle.setRadius(radius);
      }
    });

    effect(() => {
      const radius = this.radiusMeters();
      const coords = this.coordinates();
      const location = this.locationDetail();
      const fields = this.closingFields();
      if (fields.length > 0 && (coords.user || coords.formulario || coords.evidencias) && location) {
        this.buildInfoCards(fields, coords, location);
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.locationDataReady()) {
      this.initMapIfReady();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
    }
  }

  private initMapIfReady(): void {
    if (!this.mapContainer?.nativeElement || this.map) return;

    const location = this.locationDetail();
    const coords = this.coordinates();

    const lat = location?.Latitude ? Number(location.Latitude) : null;
    const lng = location?.Longitude ? Number(location.Longitude) : null;

    const defaultCenter: [number, number] = lat && lng
      ? [lat, lng]
      : coords.user || coords.formulario || coords.evidencias || [19.4326, -99.1332];

    this.map = L.map(this.mapContainer.nativeElement).setView(defaultCenter, 15);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetmap contributors',
    }).addTo(this.map);

    L.control.scale({ imperial: false, maxWidth: 100 }).addTo(this.map);

    this.updateMapMarkers(coords, location);
  }

  private updateMapMarkers(coords: { user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null }, location: Location | null): void {
    if (!this.map) return;

    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    if (this.locationCircle) {
      this.locationCircle.remove();
      this.locationCircle = null;
    }

    let colorIndex = 0;

    const addMarker = (latlng: [number, number], label: string) => {
      const marker = L.circleMarker(latlng, {
        radius: 10,
        fillColor: mapMarksColors[colorIndex % mapMarksColors.length],
        color: '#fff',
        weight: 2,
        fillOpacity: 0.9,
      }).addTo(this.map!);
      marker.bindTooltip(label, { permanent: true, direction: 'top', className: 'marker-label' });
      marker.bindPopup(label);
      this.markers.push(marker);
      colorIndex++;
    };

    if (coords.user) addMarker(coords.user, this.translationService.instant('map.userCoordinates'));
    if (coords.formulario) addMarker(coords.formulario, this.translationService.instant('map.formCoordinates'));
    if (coords.evidencias) addMarker(coords.evidencias, this.translationService.instant('map.evidenceCoordinates'));
    if (location?.Latitude && location?.Longitude) {
      addMarker([Number(location.Latitude), Number(location.Longitude)], this.translationService.instant('map.mainLocation'));

      // Add blue circle around main location
      this.locationCircle = L.circle([Number(location.Latitude), Number(location.Longitude)], {
        radius: this.radiusMeters(),
        color: mapMarksColors[0],
        fillColor: mapMarksColors[0],
        fillOpacity: 0.15,
        weight: 2,
      }).addTo(this.map);
    }

    if (this.markers.length > 0) {
      const group = L.featureGroup(this.markers);
      if (this.locationCircle) {
        group.addLayer(this.locationCircle);
      }
      this.map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  public recenterMap(): void {
    const location = this.locationDetail();
    const coords = this.coordinates();
    const lat = location?.Latitude ? Number(location.Latitude) : null;
    const lng = location?.Longitude ? Number(location.Longitude) : null;

    if (lat && lng) {
      this.map?.setView([lat, lng], 15);
    } else if (coords.user) {
      this.map?.setView(coords.user, 15);
    } else if (coords.formulario) {
      this.map?.setView(coords.formulario, 15);
    } else if (coords.evidencias) {
      this.map?.setView(coords.evidencias, 15);
    }
  }

  public centerOnPoint(point: 'user' | 'formulario' | 'evidencias' | 'main'): void {
    const location = this.locationDetail();
    const coords = this.coordinates();

    switch (point) {
      case 'user':
        if (coords.user) this.map?.setView(coords.user, 16);
        break;
      case 'formulario':
        if (coords.formulario) this.map?.setView(coords.formulario, 16);
        break;
      case 'evidencias':
        if (coords.evidencias) this.map?.setView(coords.evidencias, 16);
        break;
      case 'main':
        if (location?.Latitude && location?.Longitude) {
          this.map?.setView([Number(location.Latitude), Number(location.Longitude)], 16);
        }
        break;
    }
  }

  public fitAllMarkersMap(): void {
    const coords = this.coordinates();
    const location = this.locationDetail();
    const bounds: [number, number][] = [];

    if (coords.user) bounds.push(coords.user);
    if (coords.formulario) bounds.push(coords.formulario);
    if (coords.evidencias) bounds.push(coords.evidencias);
    if (location?.Latitude && location?.Longitude) bounds.push([Number(location.Latitude), Number(location.Longitude)]);

    if (bounds.length > 0 && this.map) {
      this.map.fitBounds(bounds as L.LatLngBoundsExpression, { padding: [20, 20] });
    }
  }

  private buildInfoCards(fields: TicketClosingField[], coords: { user: [number, number] | null; formulario: [number, number] | null; evidencias: [number, number] | null }, location: Location | null): void {
    this.infoCardsReady.set(false);
    const cards = this.applicantRecordService.buildInfoCards(fields, coords, location);
    this.infoCards.set(cards);
    this.infoCardsReady.set(true);
  }

  private buildUserProfile(fields: TicketClosingField[]): void {
    this.userProfileReady.set(false);
    const profile = this.applicantRecordService.buildUserProfile(fields);
    this.userProfile.set(profile);
    this.userProfileReady.set(true);
  }

  private async buildLocationData(fields: TicketClosingField[], location: Location): Promise<void> {
    this.locationDataReady.set(false);
    const data = await this.applicantRecordService.buildLocationData(fields, location);
    this.locationData.set(data);
    this.locationDataReady.set(true);
  }

  private buildPersonalReferences(fields: TicketClosingField[]): void {
    this.personalReferencesReady.set(false);
    const refs = this.applicantRecordService.buildPersonalReferences(fields);
    this.personalReferences.set(refs);
    this.personalReferencesReady.set(true);
  }

  private loadComments(): void {
    const validationId = this.validationTicketId();
    if (!validationId) return;

    this.commentsLoading.set(true);
    this.commentsReady.set(false);
    this.ticketService.getTicketComments(validationId).subscribe(response => {
      if (response?.Data) {
        this.comments.set(response.Data);
      }
      this.commentsReady.set(true);
      this.commentsLoading.set(false);
    });
  }

  private createComment(comment: string): void {
    const validationId = this.validationTicketId();
    if (!validationId) return;

    this.ticketService.createTicketComment(validationId, comment).subscribe(response => {
      if (response?.Result === 'CC00FF') {
        this.snackbarService.success(this.translationService.instant('applicantRecord.commentSuccess'));
        this.loadComments();
      }
    });
  }

  public onSubmitComment(): void {
    const comment = this.commentText();
    if (comment.trim() && !this.isSubmittingComment()) {
      this.isSubmittingComment.set(true);
      this.createComment(comment);
      this.commentText.set('');
      setTimeout(() => this.isSubmittingComment.set(false), 500);
    }
  }

  public openGoogleMaps(type: 'user' | 'formulario' | 'evidencias' | 'main'): void {
    const coords = this.coordinates();
    const location = this.locationDetail();

    let lat: number | null = null;
    let lng: number | null = null;

    switch (type) {
      case 'user':
        lat = coords.user?.[0] ?? null;
        lng = coords.user?.[1] ?? null;
        break;
      case 'formulario':
        lat = coords.formulario?.[0] ?? null;
        lng = coords.formulario?.[1] ?? null;
        break;
      case 'evidencias':
        lat = coords.evidencias?.[0] ?? null;
        lng = coords.evidencias?.[1] ?? null;
        break;
      case 'main':
        lat = location?.Latitude ? Number(location.Latitude) : null;
        lng = location?.Longitude ? Number(location.Longitude) : null;
        break;
    }

    if (lat && lng) {
      window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
    } else {
      this.snackbarService.error(this.translationService.instant('applicantRecord.noCoordinates'));
    }
  }
}
